import { atom, atomFamily, RecoilState } from 'recoil';
import { selector } from './recoilSelectors';
import { DEFAULT_MAP, HEAT_MAP_OBJ, MAP_TYPES_OBJ, ROAD_MAP_OBJ } from '../constants/mapConstants';
import { atomWithPersistence, readStoredValue, storageEffectForKey } from '../utils/persistence';
import { ViewState } from 'react-map-gl';
import { placesSelectedUidsState } from "./placeState";
import { pageState } from "./appState";
import { PAGE } from "../constants/pages";
import { bulkDrawPlacesState, drawModeState } from "./drawModeState";
import { Latitude, LatLng, Longitude, Uid } from "../types/core";
import { isTouchDevice, isUserFromIndia } from "../utils/app";

export const mapTypeState = atomWithPersistence('map.type', DEFAULT_MAP);

export const mapTypeObjectState = selector({
    key: "map.type.show",
    get: ({ get }) => {
        const mapType = get(mapTypeState);
        const page = get(pageState);
        if (page === PAGE.HEATMAP) {
            return HEAT_MAP_OBJ;
        } else {
            return MAP_TYPES_OBJ[mapType] || ROAD_MAP_OBJ;
        }
    }
});

export const mapViewState = atomWithPersistence<ViewState>('map.view', {
        longitude: -122.4,
        latitude: 37.8,
        zoom: 14,
    } as ViewState
);

export const zoomHighState = selector<boolean>({
    key: 'map.zoom.high',
    get: ({ get }) => {
        const zoom = get(mapViewState).zoom;
        return zoom >= 17;
    }
});

export const mapBearingState = atom({
    key: 'map.bearing',
    default: 0,
});

export const MAP_ICON_DEFAULT_SIZE = 28;

export const mapIconSizeState = atomWithPersistence('map.iconsize', MAP_ICON_DEFAULT_SIZE);

export const mapProviderState = atomWithPersistence<"mapbox" | "google">(
    'map.provider',
    (isTouchDevice() || isUserFromIndia()) ? "mapbox" : "google"
);

export const isGoogleProviderState = selector({
    key: "map.isGoogleProvider",
    get: ({ get }) => {
        return get(mapProviderState) === "google";
    }
})

export const user3DEnabledState = atomWithPersistence('map.enable3d', false);

export const is3DEnabledState = selector({
    key: "map.is3DEnabled",
    get: ({ get }) => {
        const user3DEnabled = get(user3DEnabledState);
        const selectedPlaces = get(placesSelectedUidsState);
        const drawMode = get(drawModeState);
        const page = get(pageState);
        return user3DEnabled && selectedPlaces.length === 0 && !drawMode && page !== PAGE.PLACES;
    }
})

/**
 * Layer switches are saved per board page: Live, Dispatch and Location each keep their own set,
 * every other map page shares one. The first read of a scope falls back to the old global
 * switch, so nobody loses their settings.
 */
export type MapLayerToggleId = "places" | "zones" | "traffic" | "jobs" | "routes" | "waypoints" | "members" | "tracks" | "trails" | "labels";
export type MapLayerScope = "live" | "dispatch" | "location" | "map";

const LEGACY_LAYER_KEYS: Record<MapLayerToggleId, string> = {
    places: "map.layer.places.show",
    zones: "map.layer.zones.show",
    traffic: "map.layer.external.traffic.show",
    jobs: "map.layer.jobs.show",
    routes: "map.layer.routes.show",
    waypoints: "map.layer.waypoints.show",
    members: "map.layer.members.show",
    tracks: "map.layer.tracks.show",
    trails: "map.layer.tracks.show",
    labels: "map.layer.labels.show",
};

const LAYER_DEFAULTS: Record<MapLayerToggleId, boolean> = {
    places: true,
    zones: true,
    traffic: false,
    jobs: true,
    routes: true,
    waypoints: true,
    members: true,
    tracks: true,
    trails: true,
    labels: true,
};

export const mapLayerScopeState = selector<MapLayerScope>({
    key: "map.layer.scope",
    get: ({ get }) => {
        const page = get(pageState);
        if (page === PAGE.LIVE) return "live";
        if (page === PAGE.DISPATCH) return "dispatch";
        if (page === PAGE.LOCATION) return "location";
        return "map";
    }
});

const mapLayerToggleFamily = atomFamily<boolean, { layer: MapLayerToggleId; scope: MapLayerScope }>({
    key: "map.layer.toggle",
    default: ({ layer }) => readStoredValue<boolean>(LEGACY_LAYER_KEYS[layer]) ?? LAYER_DEFAULTS[layer],
    effects: ({ layer, scope }) => [storageEffectForKey(`map.layer.${layer}.${scope}.show`)],
});

function layerToggleState(layer: MapLayerToggleId) {
    return selector<boolean>({
        key: `map.layer.${layer}.show.scoped`,
        get: ({ get }) => get(mapLayerToggleFamily({ layer, scope: get(mapLayerScopeState) })),
        set: ({ get, set }, value) => set(mapLayerToggleFamily({ layer, scope: get(mapLayerScopeState) }), value),
    });
}

export const showPlacesLayerState = layerToggleState("places");
export const showZonesLayerState = layerToggleState("zones");
export const showTrafficLayerState = layerToggleState("traffic");
export const showJobsLayerState = layerToggleState("jobs");
export const showRoutesLayerState = layerToggleState("routes");
export const showWaypointsLayerState = layerToggleState("waypoints");
export const showMembersLayerState = layerToggleState("members");
export const showTracksLayerState = layerToggleState("tracks");
export const showTrailsLayerState = layerToggleState("trails");
export const showMemberLabelsState = layerToggleState("labels");

export const MAP_LAYER_TOGGLE_STATES: Record<MapLayerToggleId, RecoilState<boolean>> = {
    places: showPlacesLayerState,
    zones: showZonesLayerState,
    traffic: showTrafficLayerState,
    jobs: showJobsLayerState,
    routes: showRoutesLayerState,
    waypoints: showWaypointsLayerState,
    members: showMembersLayerState,
    tracks: showTracksLayerState,
    trails: showTrailsLayerState,
    labels: showMemberLabelsState,
};



export const mapToolState = atom<"selection" | "point" | "route" | "ruler" | undefined>({
    key: "map.tool",
    default: undefined,
});

export const pulseMarkerState = atom<LatLng | undefined>({
    key: "map.marker.pulse",
    default: undefined,
});

export const mapMarkerUpdateEventState = atom<{ uid: Uid, lat: Latitude, lng: Longitude }>({
    key: "map.marker.event",
    default: undefined
});

export const mapSizeState = atom<{ width: number, height: number }>({
    key: "map.width",
    default: { width: 0, height: 0 },
});

export const mapMouseDownTsState = atom({
   key: "map.mouseDownTs",
   default: 0,
});

export const showMapboxLayersState = selector({
    key: "map.mapbox.showLayers",
    get: ({ get }) => {
        const drawMode = get(drawModeState);
        const bulkDrawPlaces = get(bulkDrawPlacesState);
        if ((drawMode?.type === "place" && drawMode.action === "new") || bulkDrawPlaces) {
            return false;
        }
        return true;
    }
});
